import { ReactNode } from "react";
import { CardHeader } from "@mui/material";
import ProfilePicture from "./ProfilePicture";
import CIProgression from "./CIProgression";

export interface UserData {
  name?: string;
  emoji?: string;
  extraSupportRequested?: boolean;
  hasPublicImages?: boolean;
  supporter?: boolean;
  levelStart?: string;
  levelCurrent?: string;
  intact?: boolean;
  active?: boolean;
  location?: { city?: string } | null;
}

export interface UserCardHeaderProps {
  uid: string;
  user: UserData;
  action?: ReactNode;
  classes?: {
    cardHeaderContent?: string;
  };
  onYourProfile?: boolean;
  inProgress?: boolean;
  signedIn?: boolean;
}

const UserCardHeader = ({
  uid,
  user,
  action,
  classes,
  inProgress = false,
  signedIn = false,
}: UserCardHeaderProps) => {
  // `inProgress` is already recency-gated by UserCard (active + tugged in last
  // 24h); don't OR in the raw `user.active` flag or a stale session shows a
  // perpetual "Tugging Now" pulse.
  const isActive = inProgress;

  return (
    <CardHeader
      avatar={
        <ProfilePicture
          uid={uid}
          alt={user.name}
          hasPublicImages={user.hasPublicImages}
          supporter={user.supporter}
          isActive={isActive}
          sx={{ width: 60, height: 60 }}
        />
      }
      title={
        <span style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          {/* Name shrinks with an ellipsis so a long username never wraps or
              pushes the emoji/pin away. */}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {user.name || "Restorer"}
          </span>
          <span style={{ marginLeft: 6, flexShrink: 0, whiteSpace: "nowrap" }}>
            {user.emoji}
            {signedIn && user.location && "📍"}
          </span>
        </span>
      }
      subheader={
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <CIProgression
            start={user.levelStart}
            now={user.levelCurrent}
            intact={user.intact}
          />
        </span>
      }
      action={action}
      className={classes?.cardHeaderContent}
      sx={{
        // Let the title area shrink so the name can ellipsize.
        "& .MuiCardHeader-content": { minWidth: 0 },
        "& .MuiCardHeader-action": {
          alignSelf: "center",
          // Cancel MUI's default negative right margin so the action keeps the
          // header's padding instead of riding the card edge.
          marginRight: 0,
        },
      }}
    />
  );
};

export default UserCardHeader;
